/**
 * DropdownMenu — generic popover menu, factored from the TopNav tenant/user
 * dropdown pattern (relative wrapper + fixed backdrop + .fade-in popover).
 * Used by "More" row menus, filters, the project switcher, and notifications.
 */
import { useState, type ReactNode } from "react";

import { I, type IconComponent } from "../icons";

export interface DropdownItem {
  icon?: IconComponent;
  label: string;
  onClick: () => void;
  danger?: boolean;
  active?: boolean;
}

interface DropdownMenuProps {
  /** Render the trigger; wire its onClick to `toggle`. */
  renderTrigger: (toggle: () => void, open: boolean) => ReactNode;
  items: DropdownItem[];
  align?: "left" | "right";
  width?: number;
  header?: string;
  /** Make the trigger fill its container (full-width card triggers). */
  block?: boolean;
  /** Muted line shown when `items` is empty (honest empty state). */
  emptyLabel?: string;
}

export function DropdownMenu({
  renderTrigger,
  items,
  align = "left",
  width = 210,
  header,
  block,
  emptyLabel,
}: DropdownMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const toggle = (): void => setOpen((o) => !o);

  return (
    <div
      style={{
        position: "relative",
        display: block ? "block" : "inline-flex",
        width: block ? "100%" : undefined,
      }}
    >
      {renderTrigger(toggle, open)}
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div
            className="fade-in"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              ...(align === "right" ? { right: 0 } : { left: 0 }),
              width,
              zIndex: 41,
              background: "var(--c-bg-elev)",
              border: "1px solid var(--c-line)",
              borderRadius: 10,
              boxShadow: "var(--sh-pop)",
              padding: 6,
            }}
          >
            {header && (
              <div
                style={{
                  fontSize: 10.5,
                  color: "var(--c-ink-4)",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "5px 8px 6px",
                }}
              >
                {header}
              </div>
            )}
            {items.length === 0 && emptyLabel && (
              <div style={{ padding: "10px 10px 8px", fontSize: 12.5, color: "var(--c-ink-3)" }}>
                {emptyLabel}
              </div>
            )}
            {items.map((it, i) => (
              <button
                key={i}
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                style={{
                  appearance: "none",
                  border: "none",
                  cursor: "pointer",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  textAlign: "left",
                  padding: "8px 10px",
                  borderRadius: 7,
                  background: "transparent",
                  fontFamily: "inherit",
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: it.danger ? "var(--c-err)" : "var(--c-ink)",
                  transition: "background var(--tr-fast)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = it.danger
                    ? "var(--c-err-soft)"
                    : "rgba(20,22,28,0.04)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                {it.icon && (
                  <it.icon
                    size={15}
                    style={{ color: it.danger ? "var(--c-err)" : "var(--c-ink-3)", flexShrink: 0 }}
                  />
                )}
                <span style={{ flex: 1 }}>{it.label}</span>
                {it.active && (
                  <I.Check size={14} style={{ color: "var(--c-brand)", flexShrink: 0 }} />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
